import { Component } from '@theme/component';
import { getBundleStore } from '@theme/pdp-bundle-store';

/**
 * @typedef {object} CartridgeEntry
 * @property {string} cartridgeVariantId - Variant id of the cartridge product to nest.
 * @property {number} qty - How many cartridges the subscription renews for this pack variant.
 * @property {string} sellingPlanId - Cartridge selling plan id (empty until Withings creates it).
 * @property {string} serviceSku - Withings service SKU (cartridge variant `service_sku` metafield), stamped as `_service_sku`, or null.
 * @property {string} title - Cartridge product title (order-summary label).
 * @property {number} priceCents - Nested line price estimate (unit x qty), in minor units.
 * @property {string} priceHtml - Pre-formatted money string for the order summary.
 */

/**
 * PDP controller for U-Scan cartridge products (Calci, Nutrio) — nested-subscription model,
 * same principle as the Withings+ block.
 *
 * The pack (device + first cartridges) is added ONE-TIME (no selling plan on it). This element
 * forces a NESTED cartridge line under the pack line so only the cartridges renew, never the
 * device. For the selected pack variant it reads (from a JSON map rendered by Liquid) the
 * cartridge variant to add, the renewal quantity, and the cartridge selling plan (when Withings
 * has created it). The nested line is added whenever the pack variant has a `cartridge_variant`
 * metafield set, even before a selling plan exists (it rides along as soon as one is created).
 *
 * It is a bundle-store contributor:
 *  - `getItems()` adds the cartridge variant line (qty from the metafield, + the selling plan
 *    when present). A non-empty item is what makes the bundle store commit, so the cartridge is
 *    always added for U-Scan packs.
 *  - `transformPayload()` stamps a shared `_sales_line_item_group_id` on the pack (main) line and
 *    its children, and sets `parent_id` on them so Shopify nests them under the pack (native
 *    removal cascade + grouped display).
 *  - The reader (`custom.reader_variant` on the pack PRODUCT) is added as a second child line at
 *    0 €, because Withings needs its SKU as a real order line rather than a line property.
 *  - `getDisplay()` lists the cartridge subscription AND the reader in the PDP order summary,
 *    so it matches what the cart then shows.
 *
 * @extends Component
 */
class UScanSubscription extends Component {
  #selectors = {
    map: 'script[type="application/json"]',
    variantIdInput: 'input[name="id"]',
  };

  /** @type {Record<string, CartridgeEntry>} Pack variant id -> cartridge entry. */
  #byVariant = {};

  /** @type {CartridgeEntry | null} Cartridge entry for the currently selected pack variant. */
  #current = null;

  /** @type {(() => void) | null} Bundle-store unregister handle. */
  #unregisterContributor = null;

  /**
   * Lifecycle - connected to the DOM.
   * @private
   */
  connectedCallback() {
    super.connectedCallback();
    this.#byVariant = this.#readMap();
    this.#current = this.#resolve(this.#currentVariantId());
    this.#registerWithStore();
    document.addEventListener('variant:update', this.#onVariantChange);
    document.addEventListener('model:update', this.#onVariantChange);
  }

  /**
   * Lifecycle - removed from the DOM.
   * @private
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('variant:update', this.#onVariantChange);
    document.removeEventListener('model:update', this.#onVariantChange);
    this.#unregisterContributor?.();
    this.#unregisterContributor = null;
  }

  /**
   * Parse the pack-variant -> cartridge-entry map rendered by Liquid.
   * @private
   * @returns {Record<string, CartridgeEntry>}
   */
  #readMap() {
    const script = this.querySelector(this.#selectors.map);
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return {};
    try {
      const parsed = JSON.parse(script.textContent);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /**
   * The product form for this section.
   * @private
   * @returns {HTMLFormElement | null}
   */
  #form() {
    const sectionId = this.dataset.sectionId ?? '';
    const form = document.getElementById(`BuyButtons-ProductForm-${sectionId}`);
    return form instanceof HTMLFormElement ? form : null;
  }

  /**
   * The currently selected pack variant id (form input is the source of truth).
   * @private
   * @returns {string}
   */
  #currentVariantId() {
    const input = this.#form()?.querySelector(this.#selectors.variantIdInput);
    if (input instanceof HTMLInputElement && input.value) return input.value;
    return this.dataset.defaultVariant ?? '';
  }

  /**
   * @private
   * @param {string} variantId
   * @returns {CartridgeEntry | null}
   */
  #resolve(variantId) {
    const entry = this.#byVariant[String(variantId)];
    return entry && entry.cartridgeVariantId ? entry : null;
  }

  /**
   * React to a variant change (or post-morph model update): re-read the map (the DOM may have
   * re-rendered, e.g. once a cartridge selling plan appears) and re-resolve the current entry.
   * @private
   * @param {Event} event
   */
  #onVariantChange = (event) => {
    this.#byVariant = this.#readMap();
    const detail = /** @type {CustomEvent} */ (event).detail;
    const variantId = detail?.resource?.id ? String(detail.resource.id) : this.#currentVariantId();
    this.#current = this.#resolve(variantId);
    getBundleStore().publish('uscan-subscription');
  };

  /**
   * Register the bundle-store contributor that adds and nests the cartridge line.
   * @private
   */
  #registerWithStore() {
    const store = getBundleStore();
    store.setContext({
      productId: this.dataset.productId ?? '',
      sectionId: this.dataset.sectionId ?? '',
    });

    this.#unregisterContributor = store.register({
      name: 'uscan-subscription',
      getSectionId: () => this.dataset.sectionId ?? '',
      order: 5,
      getItems: () => {
        const entry = this.#current;
        if (!entry) return [];

        /** @type {{ id: number, quantity: number, properties: Record<string, string>, selling_plan?: number }} */
        const cartridge = {
          id: Number(entry.cartridgeVariantId),
          quantity: entry.qty > 0 ? entry.qty : 1,
          properties: { _bundle_role: 'cartridge-subscription' },
        };
        if (entry.serviceSku) cartridge.properties._service_sku = entry.serviceSku;
        if (entry.sellingPlanId) cartridge.selling_plan = Number(entry.sellingPlanId);

        // The reader rides along as its own 0 € line so its SKU reaches Withings as a real
        // order line. One per pack: buyers cannot order two U-Scan at once anyway (the
        // Validation Function allows a single U-Scan line per cart).
        const readerVariantId = this.dataset.readerVariant;
        if (!readerVariantId) return [cartridge];

        return [
          cartridge,
          {
            id: Number(readerVariantId),
            quantity: 1,
            properties: { _bundle_role: 'reader' },
          },
        ];
      },
      getDisplay: () => {
        const entry = this.#current;
        if (!entry) return [];

        /** @type {Array<{ role: string, title: string, subtitle?: string, priceCents?: number, priceHtml?: string, variantId?: string }>} */
        const rows = [
          {
            role: 'cartridge-subscription',
            title: entry.title,
            subtitle: `× ${entry.qty}`,
            priceCents: entry.priceCents,
            priceHtml: entry.priceHtml,
            variantId: String(entry.cartridgeVariantId),
          },
        ];

        // The reader is a real cart line, so the summary must list it too — otherwise the PDP
        // and the cart disagree on what the pack contains. priceCents stays 0: it must not
        // move the total (the reader is included in the pack price).
        const { readerVariant, readerTitle, readerPriceHtml } = this.dataset;
        if (readerVariant && readerTitle) {
          rows.push({
            role: 'reader',
            title: readerTitle,
            subtitle: '× 1',
            priceCents: 0,
            priceHtml: readerPriceHtml ?? '',
            variantId: String(readerVariant),
          });
        }

        return rows;
      },
      transformPayload: (items, ctx) => {
        if (!this.#current) return items;
        const groupId = ctx.bundleKey;
        const mainVariantId = Number(ctx.mainVariantId);
        for (const it of items) {
          const role = it.properties?._bundle_role;
          const isChild = role === 'cartridge-subscription' || role === 'reader';
          if (role === 'main' || isChild) {
            it.properties = { ...(it.properties ?? {}), _sales_line_item_group_id: groupId };
          }
          if (isChild && mainVariantId) {
            it.parent_id = mainVariantId;
          }
        }
        return items;
      },
      onCommit: () => {
        // Restore state after a successful add (DOM may have morphed).
        this.#byVariant = this.#readMap();
        this.#current = this.#resolve(this.#currentVariantId());
        getBundleStore().publish('uscan-subscription');
      },
    });
  }
}

if (!customElements.get('uscan-subscription')) {
  customElements.define('uscan-subscription', UScanSubscription);
}
